import { DraftConfiguration, DraftState, Product, ProductTask } from '@/types';
import { createProduct } from '@/utils/http/createProduct';
import { isResponseError } from '@/utils/http/methods/responseError';
import { addProductToHistory, getExistingProductId } from '@/utils/productHistory';
import { getConfigurations } from '@/model/draft/getConfigurations';
import { mapDraftToProduct } from '@/model/draftMapper/mapDraftToProduct';
import { mapDraftToSharingDraft } from '@/model/draftMapper/mapDraftToSharingDraft';
import { isDesignConfiguration } from '@/model/draftMapper/mapProductToSharingDraft';
import { createDesignsForSpecialConfigs } from '@/model/product/createDesignsForSpecialConfigs';

export class SaveProductError extends Error {
  configurationId?: number;
  handled?: boolean;

  constructor(message: string) {
    super(message);
  }
}

export const isSaveProductError = (error: unknown): error is SaveProductError =>
  error instanceof SaveProductError;

export const getProductReference = async (
  draft: DraftState,
  progressCallback: () => void = () => {},
  onSpecialDesignsCreated?: (configurations: DraftConfiguration[], tasks: ProductTask[]) => void
) => {
  const existingProductReference = getExistingProductId(draft);
  if (existingProductReference !== null) {
    return existingProductReference;
  }

  let product: Product | undefined;
  try {
    const configurations = getConfigurations(draft);
    const tasks = await createDesignsForSpecialConfigs(configurations, progressCallback);

    onSpecialDesignsCreated?.(configurations, tasks);

    product = mapDraftToProduct(draft, tasks);
    const productReference = await createProduct(product);

    addProductToHistory(productReference, mapDraftToSharingDraft(draft));

    return productReference;
  } catch (error) {
    if (isResponseError(error)) {
      let errorData: { message?: string } = {};
      try {
        errorData = await error.response.json();
      } catch (e) {
        // ignore and throw original error
      }

      const errorMessage = errorData?.message;
      if (errorMessage) {
        const saveProductError = new SaveProductError(errorMessage);

        if (product) {
          // try to match the error from the product creation to an actual configuration
          // so we can at least hint at the broken config
          const match = errorMessage.match(/Design (\d+)/);
          if (match) {
            const designId = match[1];
            const position = product.configurations.findIndex(
              (configuration) =>
                isDesignConfiguration(configuration) &&
                configuration.content.svg.image.designId === designId
            );
            if (position !== -1) {
              saveProductError.configurationId = draft.configurationIds[position];
            }
          }
        }

        throw saveProductError;
      }
    }

    throw error;
  }
};
